import React, { useEffect, useState } from "react";
import { Controller, useForm, useFieldArray } from "react-hook-form";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  TextField,
  Typography,
  MenuItem,
  Select,
  InputLabel,
  FormHelperText,
  Grid,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Chip,
} from "@mui/material";
import { ExpandMore, Person, Check } from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { MuiTelInput } from "mui-tel-input";
import dayjs from "dayjs";
import eventService from "../services/event";

interface FormField {
  enabled: boolean;
  required: boolean;
  label: string;
  type: 'text' | 'email' | 'tel' | 'date' | 'textarea' | 'select' | 'checkbox' | 'radio';
  placeholder?: string;
  validation?: string;
  options?: string[];
}

interface FormConfig {
  fields: {
    [key: string]: FormField;
  };
  custom_fields?: FormField[];
}

interface OrderInfo {
  name: string;
  email: string;
  phone: string;
}

interface TicketHolderInfo {
  [key: string]: any;
}

interface DynamicBuyerInformationFormProps {
  eventSlug: string;
  totalTickets: number;
  cartItems?: any[]; // Add cart items to get category names
  onSubmit: (data: { 
    orderInfo: OrderInfo; 
    passengers: TicketHolderInfo[]; 
    is_news: boolean; 
    is_aggree: boolean 
  }) => void;
  isLoading: boolean;
  isVoucherMandatory?: boolean;
  voucherApplied?: boolean;
}

const DynamicBuyerInformationForm: React.FC<DynamicBuyerInformationFormProps> = ({
  eventSlug,
  totalTickets,
  cartItems = [],
  onSubmit,
  isLoading,
  isVoucherMandatory = false,
  voucherApplied = false,
}) => {
  const [formConfig, setFormConfig] = useState<FormConfig | null>(null);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [expandedPanels, setExpandedPanels] = useState<{ [key: number]: boolean }>({ 0: true });
  const [copyFeedback, setCopyFeedback] = useState<string>('');
  // Initialize form with buyer info and ticket holders arrays
  const { control, handleSubmit, watch, formState: { errors }, setValue, trigger, reset } = useForm<{
    orderInfo: OrderInfo;
    passengers: TicketHolderInfo[];
    is_news: boolean;
    is_aggree: boolean;
  }>({
    defaultValues: {
      orderInfo: {
        name: '',
        email: '',
        phone: ''
      },
      passengers: [],
      is_news: true,
      is_aggree: false,
    },
  });

  const { fields: ticketHolderFields, replace } = useFieldArray({
    control,
    name: "passengers", // Keep API field name for backend compatibility
  });

  useEffect(() => {
    if (totalTickets > 0) {
      const newTicketHolders = Array.from({ length: totalTickets }, () => ({
        gender: '', // Default empty string for select fields
        name: '',
        phone: '',
        identity_number: '',
        date_of_birth: '',
        address: '',
        emergency_contact: '',
        company: '',
        dietary_requirements: '',
        special_needs: '',
      }));
      replace(newTicketHolders);
      
      // Set first panel as expanded by default
      setExpandedPanels({ 0: true });
    }
  }, [totalTickets, replace]);

  const isAgreed = watch("is_aggree");

  // Load form configuration
  useEffect(() => {
    const loadFormConfig = async () => {
      // Guard against empty eventSlug to prevent API errors
      if (!eventSlug || eventSlug.trim() === '') {
        console.warn('EventSlug is empty, skipping form config load');
        setLoadingConfig(false);
        return;
      }

      try {
        const response = await eventService.getEventFormConfig(eventSlug);
        if (response.success) {
          setFormConfig(response.data);
        }
      } catch (error) {
        console.error("Failed to load form configuration:", error);
      } finally {
        setLoadingConfig(false);
      }
    };

    loadFormConfig();
  }, [eventSlug]);

  const handlePanelChange = (panel: number) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedPanels(prev => ({
      ...prev,
      [panel]: isExpanded
    }));
  };

  // Copy order info to all passenger forms
  const copyToAllForms = async () => {
    const orderInfoData = watch('orderInfo');
    
    if (!orderInfoData || !orderInfoData.name || !orderInfoData.phone) {
      setCopyFeedback('Please fill in the order information before copying');
      setTimeout(() => setCopyFeedback(''), 3000);
      return;
    }

    for (let i = 0; i < totalTickets; i++) {
      // Copy name and phone from order info (exclude email)
      setValue(`passengers.${i}.name` as any, orderInfoData.name);
      setValue(`passengers.${i}.phone` as any, orderInfoData.phone);
    }
    
    await trigger(); // Trigger validation
    
    setCopyFeedback(`Information copied to all ${totalTickets} ticket holder(s)!`);
    setTimeout(() => setCopyFeedback(''), 3000);
  };

  // Render different field types for each ticket holder
  const renderField = (ticketHolderIndex: number, fieldKey: string, field: FormField) => {
    const fieldName = `passengers.${ticketHolderIndex}.${fieldKey}` as any;
    const fieldKey_unique = `${ticketHolderIndex}-${fieldKey}`;

    const fieldProps = {
      name: fieldName,
      control,
      rules: { required: field.required ? `${field.label} is required` : false },
    };

    switch (field.type) {
      case 'text':
      case 'email':
        return (
          <Grid item xs={12} sm={6} key={fieldKey_unique}>
            <Controller
              {...fieldProps}
              render={({ field: fieldRender, fieldState }) => (
                <TextField
                  {...fieldRender}
                  label={field.label}
                  placeholder={field.placeholder}
                  type={field.type}
                  fullWidth
                  size="small"
                  required={field.required}
                  error={Boolean(fieldState.error)}
                  helperText={fieldState.error?.message}
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />
          </Grid>
        );

      case 'tel':
        return (
          <Grid item xs={12} sm={6} key={fieldKey_unique}>
            <Controller
              {...fieldProps}
              render={({ field: fieldRender, fieldState }) => (
                <MuiTelInput
                  {...fieldRender}
                  label={field.label}
                  fullWidth
                  size="small"
                  defaultCountry="ID"
                  required={field.required}
                  error={Boolean(fieldState.error)}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          </Grid>
        );

      case 'textarea':
        return (
          <Grid item xs={12} key={fieldKey_unique}>
            <Controller
              {...fieldProps}
              render={({ field: fieldRender, fieldState }) => (
                <TextField
                  {...fieldRender}
                  label={field.label}
                  placeholder={field.placeholder}
                  fullWidth
                  multiline
                  rows={3}
                  size="small"
                  required={field.required}
                  error={Boolean(fieldState.error)}
                  helperText={fieldState.error?.message}
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />
          </Grid>
        );

      case 'select':
        return (
          <Grid item xs={12} sm={6} key={fieldKey_unique}>
            <FormControl fullWidth size="small" required={field.required}>
              <InputLabel shrink>{field.label}</InputLabel>
              <Controller
                {...fieldProps}
                render={({ field: fieldRender, fieldState }) => (
                  <>
                    <Select
                      {...fieldRender}
                      label={field.label}
                      error={Boolean(fieldState.error)}
                      displayEmpty
                    >
                      <MenuItem value="">
                        <em>Please select...</em>
                      </MenuItem>
                      {field.options?.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </Select>
                    {fieldState.error && (
                      <FormHelperText error>{fieldState.error.message}</FormHelperText>
                    )}
                  </>
                )}
              />
            </FormControl>
          </Grid>
        );

      case 'date':
        return (
          <Grid item xs={12} sm={6} key={fieldKey_unique}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Controller
                {...fieldProps}
                render={({ field: fieldRender, fieldState }) => (
                  <DatePicker
                    {...fieldRender}
                    label={field.label}
                    format="DD/MM/YYYY"
                    value={fieldRender.value ? dayjs(fieldRender.value) : null}
                    onChange={(newValue) => fieldRender.onChange(newValue?.format('YYYY-MM-DD'))}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        size: 'small',
                        required: field.required,
                        error: Boolean(fieldState.error),
                        helperText: fieldState.error?.message,
                        InputLabelProps: { shrink: true }
                      }
                    }}
                  />
                )}
              />
            </LocalizationProvider>
          </Grid>
        );

      default:
        return null;
    }
  };

  if (loadingConfig) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <Typography>Loading form configuration...</Typography>
      </Box>
    );
  }

  if (!formConfig) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <Typography color="error">Failed to load form configuration</Typography>
      </Box>
    );
  }

  if (totalTickets === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <Typography>No tickets selected</Typography>
      </Box>
    );
  }

  if (ticketHolderFields.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <Typography>Loading ticket holder forms...</Typography>
      </Box>
    );
  }

  const enabledFields = Object.entries(formConfig.fields).filter(([_, field]) => field.enabled);

  const handleFormSubmit = (data: any) => {
    console.log('Submitting form data:', data);
    onSubmit(data);
  };

  return (
    <Box sx={{ width: '100%' }}>
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        {/* Buyer Information Section */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Person color="primary" />
              Buyer Information
            </Typography>
            
            <Alert severity="info" sx={{ mb: 3 }}>
              Tickets will be sent to the buyer's email address provided below.
            </Alert>
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="orderInfo.name"
                  control={control}
                  rules={{ required: 'Name is required' }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      label="Full Name"
                      placeholder="Enter your full name"
                      fullWidth
                      size="small"
                      required
                      error={Boolean(fieldState.error)}
                      helperText={fieldState.error?.message}
                      InputLabelProps={{ shrink: true }}
                    />
                  )}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Controller
                  name="orderInfo.email"
                  control={control}
                  rules={{ 
                    required: 'Email is required',
                    pattern: {
                      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                      message: 'Please enter a valid email address'
                    }
                  }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      label="Email Address"
                      placeholder="Enter your email address"
                      type="email"
                      fullWidth
                      size="small"
                      required
                      error={Boolean(fieldState.error)}
                      helperText={fieldState.error?.message}
                      InputLabelProps={{ shrink: true }}
                    />
                  )}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Controller
                  name="orderInfo.phone"
                  control={control}
                  rules={{ required: 'Phone number is required' }}
                  render={({ field, fieldState }) => (
                    <MuiTelInput
                      {...field}
                      label="Phone Number"
                      fullWidth
                      size="small"
                      defaultCountry="ID"
                      required
                      error={Boolean(fieldState.error)}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Ticket Holder Details Section */}
        <Typography variant="h6" gutterBottom sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Person color="primary" />
          Ticket Holder Details ({totalTickets} ticket{totalTickets > 1 ? 's' : ''})
        </Typography>

        {totalTickets > 1 && (
          <Alert severity="info" sx={{ mb: 3 }}>
            You're purchasing {totalTickets} tickets. Please provide information for each ticket holder.
            <Button 
              variant="text" 
              size="small" 
              onClick={copyToAllForms}
              sx={{ ml: 2 }}
            >
              Copy Buyer Info to All Ticket Holders
            </Button>
            {copyFeedback && (
              <Typography variant="body2" sx={{ mt: 1, color: copyFeedback.includes('copied') ? 'success.main' : 'warning.main' }}>
                {copyFeedback}
              </Typography>
            )}
          </Alert>
        )}

        {/* Ticket Holder Information Forms */}
        {ticketHolderFields.map((_, ticketHolderIndex) => {
          const isExpanded = expandedPanels[ticketHolderIndex];
          const currentTicketHolderErrors = errors.passengers?.[ticketHolderIndex];
          const hasErrors = currentTicketHolderErrors && Object.keys(currentTicketHolderErrors).length > 0;
          
          // Get category name for this ticket
          const getCategoryName = () => {
            if (cartItems && cartItems.length > 0) {
              // Assuming all tickets are from the same category (due to single category validation)
              return cartItems[0].category_name || 'Ticket';
            }
            return 'Ticket';
          };

          return (
            <Accordion 
              key={ticketHolderIndex}
              expanded={isExpanded}
              onChange={handlePanelChange(ticketHolderIndex)}
              sx={{ mb: 2, border: hasErrors ? '1px solid #f44336' : '1px solid #e0e0e0' }}
            >
              <AccordionSummary 
                expandIcon={<ExpandMore />}
                sx={{ 
                  backgroundColor: hasErrors ? '#ffebee' : isExpanded ? '#f5f5f5' : 'white',
                  '& .MuiAccordionSummary-content': {
                    alignItems: 'center',
                    gap: 2,
                  }
                }}
              >
                <Typography variant="h6">
                  Ticket Holder #{ticketHolderIndex + 1} - {getCategoryName()}
                </Typography>
                {!hasErrors && isExpanded && (
                  <Chip 
                    icon={<Check />} 
                    label="Valid" 
                    color="success" 
                    size="small" 
                  />
                )}
                {hasErrors && (
                  <Chip 
                    label="Errors" 
                    color="error" 
                    size="small" 
                  />
                )}
              </AccordionSummary>
              
              <AccordionDetails>
                <Grid container spacing={2}>
                  {enabledFields
                    .filter(([fieldKey]) => fieldKey !== 'email') // Exclude email from ticket holder details
                    .map(([fieldKey, field]) => {
                      if (!field.enabled) return null;
                      return renderField(ticketHolderIndex, fieldKey, field);
                    })}
                </Grid>
              </AccordionDetails>
            </Accordion>
          );
        })}

        {/* Agreement Section */}
        <Card sx={{ mt: 3, p: 2 }}>
          <CardContent>
            <FormControl>
              <FormControlLabel
                control={
                  <Controller
                    name="is_news"
                    control={control}
                    render={({ field }) => (
                      <Checkbox
                        {...field}
                        checked={field.value}
                        color="primary"
                      />
                    )}
                  />
                }
                label="I want to receive news and updates about future events"
              />
            </FormControl>

            <FormControl required error={Boolean(errors.is_aggree)}>
              <FormControlLabel
                control={
                  <Controller
                    name="is_aggree"
                    control={control}
                    rules={{ required: "You must agree to the terms and conditions" }}
                    render={({ field }) => (
                      <Checkbox
                        {...field}
                        checked={field.value}
                        color="primary"
                      />
                    )}
                  />
                }
                label="I agree to the terms and conditions *"
              />
              {errors.is_aggree && (
                <FormHelperText>{errors.is_aggree.message}</FormHelperText>
              )}
            </FormControl>
          </CardContent>
        </Card>

        {/* Submit Button */}
        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Box sx={{ mb: 2 }}>
            {isVoucherMandatory && !voucherApplied && (
              <Alert severity="error" sx={{ textAlign: 'left', mb: 2 }}>
                A voucher is required to proceed with this event. Please apply a valid voucher code before continuing.
              </Alert>
            )}
          </Box>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            size="large"
            disabled={!isAgreed || isLoading || (isVoucherMandatory && !voucherApplied)}
            sx={{ minWidth: 200, py: 1.5 }}
          >
            {isLoading ? "Processing..." : "Proceed to Payment"}
          </Button>
        </Box>
      </form>
    </Box>
  );
};

export default DynamicBuyerInformationForm;
